"use client";
import React, { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Link from "next/link";
import PageTransition from "@/components/animations/PageTransition";
import FadeInView from "@/components/animations/FadeInView";
import StaggerContainer from "@/components/animations/StaggerContainer";

export default function ContactPage() {
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    company: '', service: '', message: '',
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [focusedField, setFocusedField] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      setStatus(data.success ? 'success' : 'error');
    } catch {
      setStatus('error');
    }
  };

  const inputStyle = (field: string) => ({
    background: focusedField === field ? "rgba(59,130,246,0.06)" : "rgba(255,255,255,0.03)",
    border: focusedField === field ? "1px solid rgba(59,130,246,0.5)" : "1px solid rgba(255,255,255,0.08)",
    color: "#F8FAFC",
    boxShadow: focusedField === field ? "0 0 0 3px rgba(59,130,246,0.1)" : "none",
  });

  return (
    <PageTransition>
      <main style={{ background: "#070B14", overflowX: "hidden" }}>
        <Header />

        {/* Hero */}
        <section className="relative pt-36 pb-20 overflow-hidden">
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(135deg, #070B14 0%, #0B1220 50%, #070B14 100%)",
            }}
          />
          <div
            className="absolute top-1/3 right-1/4 w-[500px] h-[500px] rounded-full"
            style={{
              background:
                "radial-gradient(circle, rgba(37,99,235,0.1) 0%, transparent 70%)",
              filter: "blur(40px)",
            }}
          />
          <div
            className="absolute bottom-1/4 left-1/3 w-[400px] h-[400px] rounded-full"
            style={{
              background:
                "radial-gradient(circle, rgba(34,211,238,0.06) 0%, transparent 70%)",
              filter: "blur(40px)",
            }}
          />
          <div className="relative max-w-container mx-auto px-4 sm:px-6 text-center">
            <FadeInView delay={0}>
              <div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-pill mb-8"
                style={{
                  background: "rgba(37,99,235,0.12)",
                  border: "1px solid rgba(59,130,246,0.25)",
                }}
              >
                <span className="w-2 h-2 rounded-full bg-blue-400" />
                <span className="text-[11px] font-body font-600 uppercase tracking-widest gradient-text">
                  Get in Touch
                </span>
              </div>
            </FadeInView>
            <FadeInView delay={0.15}>
              <h1
                className="font-heading font-800 text-display-md md:text-display mb-6"
                style={{
                  color: "#F8FAFC",
                  letterSpacing: "-0.03em",
                  lineHeight: "1.05",
                }}
              >
                Let&apos;s Talk About
                <br />
                <span className="gradient-text">Your Technology</span>
              </h1>
            </FadeInView>
            <FadeInView delay={0.3}>
              <p
                className="text-body-lg max-w-2xl mx-auto"
                style={{ color: "#94A3B8" }}
              >
                Whether you&apos;re planning infrastructure, need managed IT
                support, or want a cybersecurity assessment — we&apos;re here to
                help.
              </p>
            </FadeInView>
          </div>
        </section>

        {/* Main Form + Sidebar */}
        <section id="form" className="py-16 lg:py-24">
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
              {/* Form */}
              <FadeInView className="lg:col-span-7" delay={0.1}>
                <div
                  className="rounded-2xl p-5 sm:p-8 lg:p-10"
                  style={{
                    background: "rgba(255,255,255,0.02)",
                    border: "1px solid rgba(255,255,255,0.06)",
                  }}
                >
                  {/* Step indicator */}
                  <div className="flex items-center gap-3 mb-8">
                    <div
                      className="flex items-center justify-center w-8 h-8 rounded-full text-[13px] font-heading font-700"
                      style={{
                        background: "rgba(59,130,246,0.15)",
                        color: "#60A5FA",
                        border: "1px solid rgba(59,130,246,0.3)",
                      }}
                    >
                      1
                    </div>
                    <div
                      className="h-px flex-1"
                      style={{
                        background:
                          "linear-gradient(90deg, rgba(59,130,246,0.3), rgba(59,130,246,0.05))",
                      }}
                    />
                    <h2
                      className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05]"
                      style={{ color: "#F8FAFC" }}
                    >
                      Tell Us About Your Business
                    </h2>
                  </div>

                  {status === 'success' ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.3)" }}>
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      </div>
                      <h3 className="font-heading font-700 text-[28px] mb-3" style={{ color: "#F8FAFC" }}>
                        Message received, {formData.firstName}!
                      </h3>
                      <p className="text-body-lg mb-8" style={{ color: "#94A3B8" }}>
                        We&apos;ll be in touch within 1 business day.
                      </p>
                      <Link
                        href="/book"
                        className="inline-flex items-center gap-2 text-[15px] font-heading font-600 transition-colors duration-200"
                        style={{ color: "#60A5FA" }}
                      >
                        Or book a call now
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                      </Link>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="flex flex-col gap-7">
                      {/* Name row */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-[12px] font-body font-600 uppercase tracking-wider mb-2.5" style={{ color: "rgba(148,163,184,0.7)" }}>
                            First Name <span style={{ color: "#EF4444" }}>*</span>
                          </label>
                          <input
                            type="text"
                            name="firstName"
                            required
                            placeholder="John"
                            value={formData.firstName}
                            onChange={handleChange}
                            className="w-full px-4 py-3.5 rounded-xl text-[15px] font-body outline-none transition-all duration-200"
                            style={inputStyle("firstName")}
                            onFocus={() => setFocusedField("firstName")}
                            onBlur={() => setFocusedField(null)}
                          />
                        </div>
                        <div>
                          <label className="block text-[12px] font-body font-600 uppercase tracking-wider mb-2.5" style={{ color: "rgba(148,163,184,0.7)" }}>
                            Last Name
                          </label>
                          <input
                            type="text"
                            name="lastName"
                            placeholder="Smith"
                            value={formData.lastName}
                            onChange={handleChange}
                            className="w-full px-4 py-3.5 rounded-xl text-[15px] font-body outline-none transition-all duration-200"
                            style={inputStyle("lastName")}
                            onFocus={() => setFocusedField("lastName")}
                            onBlur={() => setFocusedField(null)}
                          />
                        </div>
                      </div>

                      {/* Email + Phone */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-[12px] font-body font-600 uppercase tracking-wider mb-2.5" style={{ color: "rgba(148,163,184,0.7)" }}>
                            Business Email <span style={{ color: "#EF4444" }}>*</span>
                          </label>
                          <input
                            type="email"
                            name="email"
                            required
                            placeholder="john@company.co.nz"
                            value={formData.email}
                            onChange={handleChange}
                            className="w-full px-4 py-3.5 rounded-xl text-[15px] font-body outline-none transition-all duration-200"
                            style={inputStyle("email")}
                            onFocus={() => setFocusedField("email")}
                            onBlur={() => setFocusedField(null)}
                          />
                        </div>
                        <div>
                          <label className="block text-[12px] font-body font-600 uppercase tracking-wider mb-2.5" style={{ color: "rgba(148,163,184,0.7)" }}>
                            Phone
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            placeholder="+64 9 000 0000"
                            value={formData.phone}
                            onChange={handleChange}
                            className="w-full px-4 py-3.5 rounded-xl text-[15px] font-body outline-none transition-all duration-200"
                            style={inputStyle("phone")}
                            onFocus={() => setFocusedField("phone")}
                            onBlur={() => setFocusedField(null)}
                          />
                        </div>
                      </div>

                      {/* Company + Service */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-[12px] font-body font-600 uppercase tracking-wider mb-2.5" style={{ color: "rgba(148,163,184,0.7)" }}>
                            Company Name
                          </label>
                          <input
                            type="text"
                            name="company"
                            placeholder="Your Company Ltd"
                            value={formData.company}
                            onChange={handleChange}
                            className="w-full px-4 py-3.5 rounded-xl text-[15px] font-body outline-none transition-all duration-200"
                            style={inputStyle("company")}
                            onFocus={() => setFocusedField("company")}
                            onBlur={() => setFocusedField(null)}
                          />
                        </div>
                        <div>
                          <label className="block text-[12px] font-body font-600 uppercase tracking-wider mb-2.5" style={{ color: "rgba(148,163,184,0.7)" }}>
                            Service of Interest <span style={{ color: "#EF4444" }}>*</span>
                          </label>
                          <select
                            name="service"
                            required
                            value={formData.service}
                            onChange={handleChange}
                            className="w-full px-4 py-3.5 rounded-xl text-[15px] font-body outline-none transition-all duration-200 appearance-none"
                            style={{
                              ...inputStyle("service"),
                              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394A3B8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
                              backgroundRepeat: 'no-repeat',
                              backgroundPosition: 'right 16px center',
                            }}
                            onFocus={() => setFocusedField("service")}
                            onBlur={() => setFocusedField(null)}
                          >
                            <option value="">Select a service...</option>
                            <option>Managed IT Services</option>
                            <option>Infrastructure &amp; Technology</option>
                            <option>Network Solutions</option>
                            <option>Cybersecurity</option>
                            <option>Cloud &amp; Microsoft 365</option>
                            <option>Business Communications</option>
                            <option>AI &amp; Automation</option>
                            <option>ICT Procurement</option>
                            <option>General Enquiry</option>
                          </select>
                        </div>
                      </div>

                      {/* Message */}
                      <div>
                        <label className="block text-[12px] font-body font-600 uppercase tracking-wider mb-2.5" style={{ color: "rgba(148,163,184,0.7)" }}>
                          Message
                        </label>
                        <textarea
                          name="message"
                          rows={4}
                          placeholder="Tell us about your current IT setup and what you're looking to achieve..."
                          value={formData.message}
                          onChange={handleChange}
                          className="w-full px-4 py-3.5 rounded-xl text-[15px] font-body outline-none transition-all duration-200 resize-none"
                          style={inputStyle("message")}
                          onFocus={() => setFocusedField("message")}
                          onBlur={() => setFocusedField(null)}
                        />
                      </div>

                      {/* Error message */}
                      {status === 'error' && (
                        <div className="rounded-xl p-4 flex items-start gap-3" style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)" }}>
                          <span className="text-[18px] flex-shrink-0">&#9888;</span>
                          <p className="text-[14px] font-body" style={{ color: "#FCA5A5" }}>
                            Something went wrong — please try again or call us on <a href="tel:0800776481" className="font-600 underline">0800 77 6481</a>.
                          </p>
                        </div>
                      )}

                      {/* Submit */}
                      <button
                        type="submit"
                        disabled={status === 'loading'}
                        className="group relative w-full py-4 text-[15px] font-heading font-600 rounded-xl overflow-hidden transition-all duration-300"
                        style={{
                          background: "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
                          color: "#F8FAFC",
                          boxShadow: "0 0 24px rgba(37,99,235,0.3), 0 4px 12px rgba(0,0,0,0.2)",
                          opacity: status === 'loading' ? 0.6 : 1,
                          cursor: status === 'loading' ? 'not-allowed' : 'pointer',
                        }}
                        onMouseEnter={(e) => {
                          if (status !== 'loading') {
                            (e.currentTarget as HTMLButtonElement).style.boxShadow = "0 0 32px rgba(37,99,235,0.5), 0 8px 24px rgba(0,0,0,0.3)";
                            (e.currentTarget as HTMLButtonElement).style.transform = "translateY(-1px)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLButtonElement).style.boxShadow = "0 0 24px rgba(37,99,235,0.3), 0 4px 12px rgba(0,0,0,0.2)";
                          (e.currentTarget as HTMLButtonElement).style.transform = "translateY(0)";
                        }}
                      >
                        <span className="relative z-10 flex items-center justify-center gap-2">
                          {status === 'loading' ? 'Sending...' : (
                            <>
                              Send Message
                              <svg
                                width="18" height="18" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" strokeWidth="2"
                                strokeLinecap="round" strokeLinejoin="round"
                                className="transition-transform duration-300 group-hover:translate-x-1"
                              >
                                <path d="M5 12h14M12 5l7 7-7 7" />
                              </svg>
                            </>
                          )}
                        </span>
                      </button>
                    </form>
                  )}
                </div>
              </FadeInView>

              {/* Sidebar */}
              <div className="lg:col-span-5 flex flex-col gap-6">
                {/* Contact Options */}
                <FadeInView delay={0.15}>
                  <StaggerContainer
                    className="grid grid-cols-1 gap-4"
                    staggerDelay={0.08}
                  >
                    {[
                      {
                        icon: "📅",
                        title: "Book a Strategy Call",
                        desc: "Schedule a call to discuss your technology requirements.",
                        accent: "#3B82F6",
                      },
                      {
                        icon: "💰",
                        title: "Request a Quote",
                        desc: "Get a detailed quote for your IT project or services.",
                        accent: "#22D3EE",
                      },
                      {
                        icon: "💬",
                        title: "General Enquiry",
                        desc: "Have a question? We'll get back to you promptly.",
                        accent: "#10B981",
                      },
                    ].map((opt) => (
                      <a
                        key={opt.title}
                        href="#form"
                        className="flex items-start gap-4 rounded-xl p-5 transition-all duration-300 group"
                        style={{
                          background: "rgba(255,255,255,0.02)",
                          border: "1px solid rgba(255,255,255,0.06)",
                        }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLAnchorElement).style.borderColor = `${opt.accent}40`;
                          (e.currentTarget as HTMLAnchorElement).style.background = `${opt.accent}08`;
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(255,255,255,0.06)";
                          (e.currentTarget as HTMLAnchorElement).style.background = "rgba(255,255,255,0.02)";
                        }}
                      >
                        <div
                          className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center text-lg"
                          style={{ background: `${opt.accent}15` }}
                        >
                          {opt.icon}
                        </div>
                        <div>
                          <h3
                            className="font-heading font-600 text-[16px] mb-1"
                            style={{ color: "#F8FAFC" }}
                          >
                            {opt.title}
                          </h3>
                          <p
                            className="text-[14px] font-body leading-[1.75]"
                            style={{ color: "#94A3B8" }}
                          >
                            {opt.desc}
                          </p>
                        </div>
                      </a>
                    ))}
                  </StaggerContainer>
                </FadeInView>

                {/* Contact Details */}
                <FadeInView delay={0.25}>
                  <div
                    className="rounded-xl p-6"
                    style={{
                      background: "rgba(255,255,255,0.02)",
                      border: "1px solid rgba(255,255,255,0.06)",
                    }}
                  >
                    <h3
                      className="font-heading font-600 text-[16px] mb-5"
                      style={{ color: "#F8FAFC" }}
                    >
                      Contact Details
                    </h3>
                    <div className="flex flex-col gap-4">
                      {[
                        {
                          icon: "📧",
                          label: "Email",
                          value: "info@proitsolutions.nz",
                          href: "mailto:info@proitsolutions.nz",
                        },
                        {
                          icon: "📞",
                          label: "Phone",
                          value: "0800 77 6481",
                          href: "tel:0800776481",
                        },
                        {
                          icon: "📍",
                          label: "Location",
                          value: "Albany, Auckland, NZ",
                        },
                        {
                          icon: "🕐",
                          label: "Hours",
                          value: "Mon–Fri, 8am–6pm NZST",
                        },
                      ].map((item) => (
                        <div key={item.label} className="flex items-center gap-3">
                          <span
                            className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-[15px]"
                            style={{ background: "rgba(59,130,246,0.08)" }}
                          >
                            {item.icon}
                          </span>
                          <div>
                            <p
                              className="text-[11px] font-body uppercase tracking-wider"
                              style={{ color: "rgba(148,163,184,0.5)" }}
                            >
                              {item.label}
                            </p>
                            {item.href ? (
                              <a
                                href={item.href}
                                className="text-[14px] font-body font-medium transition-colors duration-200 hover:text-blue-400"
                                style={{ color: "#CBD5E1" }}
                              >
                                {item.value}
                              </a>
                            ) : (
                              <p
                                className="text-[14px] font-body font-medium"
                                style={{ color: "#CBD5E1" }}
                              >
                                {item.value}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </FadeInView>

                {/* Response Time */}
                <FadeInView delay={0.35}>
                  <div
                    className="rounded-xl p-5 flex items-center gap-4"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(37,99,235,0.06) 0%, rgba(34,211,238,0.04) 100%)",
                      border: "1px solid rgba(59,130,246,0.15)",
                    }}
                  >
                    <div
                      className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{
                        background: "rgba(59,130,246,0.1)",
                        border: "1px solid rgba(59,130,246,0.2)",
                      }}
                    >
                      <span className="text-[20px]">⚡</span>
                    </div>
                    <div>
                      <p
                        className="text-[14px] font-heading font-600"
                        style={{ color: "#F8FAFC" }}
                      >
                        &lt;2hr Response Time
                      </p>
                      <p
                        className="text-[14px] font-body"
                        style={{ color: "#94A3B8" }}
                      >
                        During NZ business hours
                      </p>
                    </div>
                  </div>
                </FadeInView>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </PageTransition>
  );
}
